import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getFeatureConfig,
  fetchFeatureConfig,
  saveFeatureConfig,
} from '../utils/featureConfig';
import './Config.css';

const DEFAULT_CONFIG = {
  hideCrimeOverlay: false,
  hideSafetyScore: false,
  hideAffordabilityScore: false,
  hideLivabilityScore: false,
  hideFurnishedFinder: false,
};

export default function Config() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [config, setConfig] = useState(() => getFeatureConfig());

  useEffect(() => {
    fetchFeatureConfig().then((cfg) => setConfig(cfg));
  }, []);

  const rows = useMemo(
    () => [
      {
        key: 'hideCrimeOverlay',
        label: 'Hide crime overlay',
        help: 'Removes crime map layer and crime opacity control.',
      },
      {
        key: 'hideSafetyScore',
        label: 'Hide Safety score',
        help: 'Keeps Location Rating, Cost, and Livability visible.',
      },
      {
        key: 'hideAffordabilityScore',
        label: 'Hide Cost score',
        help: 'Keeps Location Rating, Safety, and Livability visible.',
      },
      {
        key: 'hideLivabilityScore',
        label: 'Hide Livability score',
        help: 'Keeps Location Rating, Safety, and Cost visible.',
      },
      {
        key: 'hideFurnishedFinder',
        label: 'Hide Furnished Finder',
        help: 'Hides FF section on Search and FF controls in Filters.',
      },
    ],
    []
  );

  const handleUnlock = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const current = await saveFeatureConfig(config, password);
      setConfig(current);
      setUnlocked(true);
      setStatus('Unlocked');
    } catch {
      setError('Incorrect password.');
      setStatus('');
    }
  };

  const saveConfig = async (next) => {
    setConfig(next);
    setStatus('Saving...');
    setError('');
    try {
      const saved = await saveFeatureConfig(next, password);
      setConfig(saved);
      setStatus('Saved');
    } catch (err) {
      setStatus('');
      setError(err.message || 'Failed to save');
    }
  };

  const handleToggle = async (key) => {
    await saveConfig({ ...config, [key]: !config[key] });
  };

  const handleShowEverything = async () => {
    await saveConfig({ ...DEFAULT_CONFIG });
  };

  const handleHideMostOptional = async () => {
    await saveConfig({
      hideCrimeOverlay: true,
      hideSafetyScore: true,
      hideAffordabilityScore: false,
      hideLivabilityScore: false,
      hideFurnishedFinder: true,
    });
  };

  return (
    <div className="config-page">
      <div className="config-page__back">
        <Link to="/">← Back to home</Link>
      </div>

      <div className="config-panel config-panel--header">
        <h1>Configuration</h1>
        <p>Global toggles (applies to all users viewing this environment).</p>
      </div>

      {!unlocked ? (
        <form onSubmit={handleUnlock} className="config-panel config-panel--unlock">
          <label htmlFor="cfg-password" className="config-panel__label">Password</label>
          <input
            id="cfg-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="config-panel__input"
          />
          <button type="submit" className="config-btn">Unlock</button>
          {error && <div className="config-error">{error}</div>}
        </form>
      ) : (
        <div className="config-toggles">
          <div className="config-panel config-panel--actions">
            <button type="button" onClick={handleShowEverything} className="config-btn config-btn--secondary">
              Show everything
            </button>
            <button type="button" onClick={handleHideMostOptional} className="config-btn config-btn--secondary">
              Hide optional sections
            </button>
          </div>

          {rows.map((row) => (
            <label key={row.key} className="config-panel config-toggle-row">
              <span className="config-toggle-row__title">
                <input
                  type="checkbox"
                  checked={!!config[row.key]}
                  onChange={() => handleToggle(row.key)}
                />
                {row.label}
              </span>
              <span className="config-toggle-row__help">{row.help}</span>
            </label>
          ))}

          {status && <div className="config-status">{status}</div>}
          {error && <div className="config-error">{error}</div>}
        </div>
      )}
    </div>
  );
}
